import React, { useState, useEffect, useCallback, useMemo } from "react";
import { bookingsAPI, activityLogAPI } from "../services/api";
import Pagination from "../components/ui/Pagination";
import { localDateStr } from "../utils/date";

const PAGE_SIZE = 10;

function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("vi-VN", { hour12: false });
}

function shortId(id: string | undefined): string {
  return id ? "#" + id.slice(-6).toUpperCase() : "—";
}

interface EditHistoryEntry {
  editedAt: string;
  editedBy?: string;
  reason?: string;
  changes?: string;
}

interface BookingWithHistory {
  _id: string;
  roomName?: string;
  guestName?: string;
  checkInDate?: string;
  checkInTime?: string;
  editHistory?: EditHistoryEntry[];
}

interface FlatEditRecord {
  bookingId: string;
  roomName: string;
  guestName: string;
  checkInDate: string;
  checkInTime: string;
  editedAt: string;
  editedBy: string;
  reason: string;
  changes: string;
}

interface ChangeEntry {
  field: string;
  oldValue: string;
  newValue: string;
}

interface ActivityLogEntry {
  _id: string;
  performedBy: string;
  action: string;
  targetType: string;
  targetId?: string;
  targetName?: string;
  detail?: string;
  changes?: ChangeEntry[];
  performedAt: string;
}

// ── Stats Card ───────────────────────────────────────────────────────────────
interface StatCardProps {
  icon: string;
  label: string;
  value: number;
  color: string;
}

function StatCard({ icon, label, value, color }: StatCardProps): React.ReactElement {
  return (
    <div className={`rounded-xl p-4 ${color} flex items-center gap-4`}>
      <span className="text-3xl">{icon}</span>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm opacity-70">{label}</p>
      </div>
    </div>
  );
}

// ── Action badge ─────────────────────────────────────────────────────────────
function ActionBadge({ action }: { action: string }): React.ReactElement {
  const map: Record<string, { label: string; cls: string }> = {
    CREATE_VOUCHER:  { label: "Tạo voucher",    cls: "bg-green-100 text-green-700" },
    UPDATE_VOUCHER:  { label: "Sửa voucher",    cls: "bg-yellow-100 text-yellow-700" },
    DELETE_VOUCHER:  { label: "Xóa voucher",    cls: "bg-red-100 text-red-700" },
    TOGGLE_VOUCHER:  { label: "Bật/tắt voucher", cls: "bg-orange-100 text-orange-700" },
    UPDATE_ROOM:     { label: "Sửa phòng",      cls: "bg-coffee-100 text-coffee-700" },
    UPDATE_BOOKING:  { label: "Sửa đơn đặt",   cls: "bg-coffee-100 text-coffee-700" },
  };
  const info = map[action] || { label: action, cls: "bg-gray-100 text-gray-700" };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${info.cls}`}>
      {info.label}
    </span>
  );
}

// ── Changes display ───────────────────────────────────────────────────────────
function ChangesDisplay({ changes }: { changes?: ChangeEntry[] }): React.ReactElement {
  if (!changes || changes.length === 0) {
    return <span className="text-xs text-gray-400 italic">Không có chi tiết</span>;
  }
  return (
    <div className="space-y-1">
      {changes.map((c, i) => (
        <div key={i} className="text-xs rounded-lg overflow-hidden border border-gray-100">
          <div className="px-2 py-0.5 bg-gray-50 text-gray-500 font-medium">{c.field}</div>
          <div className="px-2 py-1 flex items-center gap-1.5 flex-wrap">
            <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded line-through max-w-[160px] truncate" title={c.oldValue}>
              {c.oldValue || "(trống)"}
            </span>
            <span className="text-gray-400">→</span>
            <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-medium max-w-[160px] truncate" title={c.newValue}>
              {c.newValue || "(trống)"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EditHistoryPage(): React.ReactElement {
  const [bookings, setBookings] = useState<BookingWithHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [filterRoom, setFilterRoom] = useState<string>("all");
  const [filterEditor, setFilterEditor] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState<boolean>(true);
  const [logSearch, setLogSearch] = useState<string>("");
  const [logFilterType, setLogFilterType] = useState<string>("all");
  const [editPage, setEditPage] = useState(1);
  const [logPage, setLogPage] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await bookingsAPI.getAll();
      setBookings(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActivityLogs = useCallback(async () => {
    try {
      setActivityLogsLoading(true);
      const res = await activityLogAPI.getAll();
      setActivityLogs(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setActivityLogsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadActivityLogs(); }, [loadActivityLogs]);

  // Flatten tất cả editHistory từ mọi booking thành 1 mảng phẳng
  const allEdits = useMemo((): FlatEditRecord[] => {
    const list: FlatEditRecord[] = [];
    bookings.forEach((b) => {
      (b.editHistory || []).forEach((h) => {
        list.push({
          bookingId:   b._id,
          roomName:    b.roomName || "",
          guestName:   b.guestName || "—",
          checkInDate: b.checkInDate || "",
          checkInTime: b.checkInTime || "",
          editedAt:    h.editedAt,
          editedBy:    h.editedBy || "—",
          reason:      h.reason   || "—",
          changes:     h.changes  || "",
        });
      });
    });
    // Sắp xếp mới nhất lên đầu
    return list.sort((a, b) => new Date(b.editedAt).getTime() - new Date(a.editedAt).getTime());
  }, [bookings]);

  // Danh sách filter
  const rooms   = useMemo(() => Array.from(new Set(allEdits.map(e => e.roomName))).sort(), [allEdits]);
  const editors = useMemo(() => Array.from(new Set(allEdits.map(e => e.editedBy))).sort(),  [allEdits]);

  // Lọc booking edits
  const filtered = useMemo(() => {
    return allEdits.filter((e) => {
      if (filterRoom   !== "all" && e.roomName  !== filterRoom)   return false;
      if (filterEditor !== "all" && e.editedBy  !== filterEditor) return false;
      if (fromDate && e.editedAt < fromDate) return false;
      if (toDate   && e.editedAt > toDate + "T23:59:59") return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          e.guestName.toLowerCase().includes(q) ||
          e.editedBy.toLowerCase().includes(q)  ||
          e.reason.toLowerCase().includes(q)    ||
          e.changes.toLowerCase().includes(q)   ||
          e.roomName.toLowerCase().includes(q)  ||
          e.bookingId.slice(-6).toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allEdits, filterRoom, filterEditor, fromDate, toDate, search]);

  // Filter activity logs
  const filteredLogs = useMemo(() => {
    return activityLogs.filter((log) => {
      if (logFilterType !== "all" && !log.action.includes(logFilterType)) return false;
      if (logSearch) {
        const q = logSearch.toLowerCase();
        return (
          log.performedBy.toLowerCase().includes(q) ||
          (log.targetName || "").toLowerCase().includes(q) ||
          (log.detail || "").toLowerCase().includes(q) ||
          log.action.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [activityLogs, logSearch, logFilterType]);

  useEffect(() => { setEditPage(1); }, [filtered]);
  useEffect(() => { setLogPage(1); }, [filteredLogs]);

  const editTotalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const logTotalPages  = Math.ceil(filteredLogs.length / PAGE_SIZE);
  const pageEdits = useMemo(() => filtered.slice((editPage - 1) * PAGE_SIZE, editPage * PAGE_SIZE), [filtered, editPage]);
  const pageLogs  = useMemo(() => filteredLogs.slice((logPage - 1) * PAGE_SIZE, logPage * PAGE_SIZE), [filteredLogs, logPage]);

  // Stats
  const uniqueEditors = useMemo(() => new Set(allEdits.map(e => e.editedBy)).size, [allEdits]);
  const todayStr = localDateStr();
  const todayCount = useMemo(
    () => allEdits.filter(e => (e.editedAt ? localDateStr(new Date(e.editedAt)) : "") === todayStr).length,
    [allEdits, todayStr]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Lịch sử chỉnh sửa</h1>
        <p className="text-gray-500 mt-1">Toàn bộ thao tác chỉnh sửa đơn đặt phòng</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon="✏️" label="Tổng lần sửa"   value={allEdits.length}  color="bg-coffee-50 text-coffee-700" />
        <StatCard icon="📋" label="Đơn đã sửa"     value={bookings.filter(b => (b.editHistory?.length || 0) > 0).length} color="bg-coffee-50 text-coffee-700" />
        <StatCard icon="👤" label="Người sửa"       value={uniqueEditors}   color="bg-green-50 text-green-700" />
        <StatCard icon="📅" label="Hôm nay"         value={todayCount}      color="bg-orange-50 text-orange-700" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Tìm kiếm tên khách, người sửa, lý do..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300"
            />
          </div>

          {/* Room filter */}
          <select
            value={filterRoom}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterRoom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300 bg-white"
          >
            <option value="all">Tất cả phòng</option>
            {rooms.map(r => <option key={r} value={r}>{r}</option>)}
          </select>

          {/* Editor filter */}
          <select
            value={filterEditor}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilterEditor(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300 bg-white"
          >
            <option value="all">Tất cả người sửa</option>
            {editors.map(e => <option key={e} value={e}>{e}</option>)}
          </select>

          {/* Date range */}
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={fromDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFromDate(e.target.value)}
              className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-coffee-300"
            />
            <span className="text-gray-400 text-xs">→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToDate(e.target.value)}
              className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-coffee-300"
            />
          </div>
        </div>

        {/* Reset + count */}
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm text-gray-500">
            Hiển thị <span className="font-semibold text-gray-800">{filtered.length}</span> / {allEdits.length} lần sửa
          </p>
          {(search || filterRoom !== "all" || filterEditor !== "all" || fromDate || toDate) && (
            <button
              onClick={() => { setSearch(""); setFilterRoom("all"); setFilterEditor("all"); setFromDate(""); setToDate(""); }}
              className="text-xs text-coffee-600 hover:underline"
            >
              Xóa bộ lọc
            </button>
          )}
        </div>
      </div>

      {/* Booking edit history Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coffee-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">
            {allEdits.length === 0 ? "Chưa có lần chỉnh sửa nào" : "Không có kết quả phù hợp"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Thời gian</th>
                  <th className="px-4 py-3 font-medium">Người sửa</th>
                  <th className="px-4 py-3 font-medium">Mã đơn</th>
                  <th className="px-4 py-3 font-medium">Phòng</th>
                  <th className="px-4 py-3 font-medium">Khách hàng</th>
                  <th className="px-4 py-3 font-medium">Lý do</th>
                  <th className="px-4 py-3 font-medium">Nội dung thay đổi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageEdits.map((e, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">

                    {/* Thời gian */}
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {formatDateTime(e.editedAt)}
                    </td>

                    {/* Người sửa */}
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-coffee-100 text-coffee-700 rounded-full text-xs font-medium">
                        👤 {e.editedBy}
                      </span>
                    </td>

                    {/* Mã đơn */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-coffee-600 font-semibold">
                        {shortId(e.bookingId)}
                      </span>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {e.checkInDate} {e.checkInTime}
                      </p>
                    </td>

                    {/* Phòng */}
                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                      {e.roomName}
                    </td>

                    {/* Khách hàng */}
                    <td className="px-4 py-3 text-gray-700">
                      {e.guestName}
                    </td>

                    {/* Lý do */}
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="text-gray-700 text-xs leading-relaxed line-clamp-2" title={e.reason}>
                        {e.reason}
                      </p>
                    </td>

                    {/* Nội dung thay đổi */}
                    <td className="px-4 py-3 max-w-[280px]">
                      {e.changes ? (
                        <div className="space-y-1">
                          {e.changes.split(" | ").map((c, ci) => {
                            // Parse "Label: OLD → NEW" format
                            const arrowIdx = c.indexOf(" → ");
                            if (arrowIdx > -1) {
                              const colonIdx = c.indexOf(": ");
                              const field = colonIdx > -1 ? c.slice(0, colonIdx) : "";
                              const rest = colonIdx > -1 ? c.slice(colonIdx + 2) : c;
                              const [oldVal, newVal] = rest.split(" → ");
                              return (
                                <div key={ci} className="text-xs rounded-lg overflow-hidden border border-gray-100">
                                  {field && <div className="px-2 py-0.5 bg-gray-50 text-gray-500 font-medium">{field}</div>}
                                  <div className="px-2 py-1 flex items-center gap-1.5 flex-wrap">
                                    <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded line-through max-w-[120px] truncate" title={oldVal}>{oldVal || "(trống)"}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-medium max-w-[120px] truncate" title={newVal}>{newVal || "(trống)"}</span>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <p key={ci} className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-0.5 leading-relaxed">
                                {c}
                              </p>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Không có chi tiết</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={editPage} totalPages={editTotalPages} total={filtered.length} pageSize={PAGE_SIZE} onChange={setEditPage} />
      </div>

      {/* Activity Logs Section */}
      <div className="mt-8">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Lịch sử hoạt động</h2>
          <p className="text-gray-500 mt-1">Tất cả thao tác: chỉnh sửa phòng, đơn đặt, voucher — kèm chi tiết thay đổi</p>
        </div>

        {/* Filters for activity log */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Tìm theo người thực hiện, tên phòng/đơn..."
              value={logSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLogSearch(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <select
              value={logFilterType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLogFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            >
              <option value="all">Tất cả loại</option>
              <option value="ROOM">Phòng</option>
              <option value="BOOKING">Đơn đặt</option>
              <option value="VOUCHER">Voucher</option>
            </select>
            {(logSearch || logFilterType !== "all") && (
              <button
                onClick={() => { setLogSearch(""); setLogFilterType("all"); }}
                className="text-xs text-coffee-600 hover:underline whitespace-nowrap"
              >
                Xóa bộ lọc
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Hiển thị <span className="font-semibold text-gray-800">{filteredLogs.length}</span> / {activityLogs.length} hoạt động
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {activityLogsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coffee-600" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              Chưa có hoạt động nào được ghi lại
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 font-medium">Thời gian</th>
                    <th className="px-4 py-3 font-medium">Người thực hiện</th>
                    <th className="px-4 py-3 font-medium">Hành động</th>
                    <th className="px-4 py-3 font-medium">Đối tượng</th>
                    <th className="px-4 py-3 font-medium">Chi tiết / Ghi chú</th>
                    <th className="px-4 py-3 font-medium">Thay đổi (từ → đến)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageLogs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {formatDateTime(log.performedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-coffee-100 text-coffee-700 rounded-full text-xs font-medium">
                          👤 {log.performedBy}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ActionBadge action={log.action} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 max-w-[160px]">
                        {log.targetName ? (
                          <span className="font-medium">{log.targetName}</span>
                        ) : log.targetId ? (
                          <span className="font-mono text-gray-500">{shortId(log.targetId)}</span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-[180px]">
                        <p className="line-clamp-2" title={log.detail}>{log.detail || "—"}</p>
                      </td>
                      <td className="px-4 py-3 max-w-[300px]">
                        <ChangesDisplay changes={log.changes} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={logPage} totalPages={logTotalPages} total={filteredLogs.length} pageSize={PAGE_SIZE} onChange={setLogPage} />
        </div>
      </div>
    </div>
  );
}
